/**
 * ApiErrorNotice — renders error messages with context-specific UX.
 * Handles special cases like 401 (sign-in) and 403 (access denied). 402
 * insufficient_credits no longer renders here — that's an offer surface,
 * not an error, and now renders through PaywallOffer instead (#943).
 */
import React from 'react';
import { ApiError } from '../utils/api-error';
import { T } from '../designTokens';

export interface ApiErrorNoticeProps {
  error: Error | null;
  onRetry?: () => void;
  message?: string;
}

export default function ApiErrorNotice({ error, onRetry, message: messageProp }: ApiErrorNoticeProps) {
  if (!error) {
    return null;
  }

  const isApiError = error instanceof ApiError;
  const status = isApiError ? error.status : 0;

  let title = 'Error';
  let message = messageProp !== undefined ? messageProp : error.message;

  // Special case: 401 Unauthorized
  if (status === 401) {
    title = 'Sign in required';
    message = messageProp !== undefined ? messageProp : 'Your session has expired or this resource is private. Sign in and try again.';
  }

  // Special case: 403 Forbidden
  if (status === 403) {
    title = 'Access denied';
    message = messageProp !== undefined ? messageProp : error.message;
  }

  return (
    <div
      role="alert"
      style={{
        backgroundColor: T.errorBg,
        border: `1px solid ${T.errorBorder}`,
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        color: T.error,
      }}
    >
      {/* Default rendering: title + message */}
      <strong>{title}:</strong> {message}

      {/* Retry button */}
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            marginTop: '12px',
            marginLeft: '12px',
            padding: '8px 16px',
            backgroundColor: T.info,
            color: T.white,
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Try again
        </button>
      )}
    </div>
  );
}
